import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS, APP_INPUT_CLASS } from '../../../ui/controlStyles';

export type EInvoiceTaxIds = { vat_id: string; commercial_register_number: string };

type Props = {
  open: boolean;
  vatMode: string;
  organization: { id: string; name: string } | null;
  initial: EInvoiceTaxIds;
  /** Absender ohne passende Kennung (Einstellungen → Firma), nur Hinweis. */
  sellerMissing: boolean;
  onClose: () => void;
  /** PDF ohne E-Rechnungs-XML erzeugen. */
  onSkip: () => void;
  onSaved: (ids: EInvoiceTaxIds) => void;
};

/**
 * Vor der PDF-Vorschau: Reverse Charge / innergemeinschaftliche Lieferung brauchen beim Kunden
 * USt-IdNr. (bzw. bei § 13b alternativ die Handelsregisternr.) — sonst gibt es kein ZUGFeRD-XML.
 */
export function EInvoiceTaxIdDialog({
  open,
  vatMode,
  organization,
  initial,
  sellerMissing,
  onClose,
  onSkip,
  onSaved,
}: Props) {
  const [vatId, setVatId] = React.useState(initial.vat_id);
  const [registerNumber, setRegisterNumber] = React.useState(initial.commercial_register_number);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setVatId(initial.vat_id);
    setRegisterNumber(initial.commercial_register_number);
    setError(null);
  }, [open, initial.vat_id, initial.commercial_register_number]);

  const isReverseCharge = vatMode === 'vat_0_reverse_charge';
  const buyerReady = vatId.trim() !== '' || (isReverseCharge && registerNumber.trim() !== '');
  const labelClass = 'text-xs font-medium text-app-text-muted';

  const save = async () => {
    if (!organization || !buyerReady) return;
    setSaving(true);
    setError(null);
    try {
      const next = { vat_id: vatId.trim(), commercial_register_number: registerNumber.trim() };
      const res = await fetch(`/api/organizations/${encodeURIComponent(organization.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(isReverseCharge ? next : { vat_id: next.vat_id }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Organisation konnte nicht gespeichert werden.');
      }
      onSaved(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Organisation konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      disableClose={saving}
      overlayClassName="z-[300]"
      panelClassName="md:max-w-lg md:w-full md:overflow-hidden"
      ariaLabelledBy="einvoice-taxid-title"
    >
      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-amber-100 bg-amber-50 px-5 py-4 dark:border-amber-900 dark:bg-amber-950/40">
          <div className="flex gap-2.5">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <h2 id="einvoice-taxid-title" className="text-base font-semibold text-amber-950 dark:text-amber-100">
                Kennung für die E-Rechnung fehlt
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-amber-950 dark:text-amber-100">
                {isReverseCharge
                  ? 'Bei Reverse Charge (§ 13b) braucht die E-Rechnung die USt-IdNr. oder die Handelsregisternummer des Kunden. Die Steuernummer reicht nicht.'
                  : 'Bei innergemeinschaftlicher Lieferung braucht die E-Rechnung die USt-IdNr. des Kunden.'}{' '}
                Ohne diese Angabe wird nur ein PDF ohne E-Rechnungsdaten erzeugt.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md p-1.5 text-amber-900 hover:bg-amber-100 disabled:opacity-40 dark:text-amber-100 dark:hover:bg-amber-900/40"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {organization ? (
            <>
              <p className="text-sm text-app-text-muted">
                Wird bei <span className="font-medium text-app-text">{organization.name}</span> gespeichert.
              </p>
              <label className="block">
                <span className={labelClass}>USt-IdNr.</span>
                <input
                  value={vatId}
                  onChange={(e) => setVatId(e.target.value)}
                  placeholder="DE123456789"
                  autoFocus
                  className={`${APP_INPUT_CLASS} mt-1`}
                />
              </label>
              {isReverseCharge ? (
                <label className="block">
                  <span className={labelClass}>oder Handelsregisternr.</span>
                  <input
                    value={registerNumber}
                    onChange={(e) => setRegisterNumber(e.target.value)}
                    placeholder="HRB 12345, Amtsgericht Berlin"
                    className={`${APP_INPUT_CLASS} mt-1`}
                  />
                  <span className="mt-1 block text-xs text-app-text-subtle">
                    Bei GmbH, UG oder e. K. steht sie im Impressum oder unter handelsregister.de.
                  </span>
                </label>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-app-text-muted">
              Dem Projekt ist keine Organisation zugeordnet. Bitte den Kunden als Organisation anlegen und die
              Kennung dort eintragen.
            </p>
          )}
          {sellerMissing ? (
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {isReverseCharge
                ? 'Auch Ihre Firma braucht eine USt-IdNr. oder Steuernummer. Bitte unter Einstellungen → Firma ergänzen.'
                : 'Auch Ihre Firma braucht eine USt-IdNr. Bitte unter Einstellungen → Firma ergänzen.'}
            </p>
          ) : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-app-border px-5 py-3">
          <button type="button" onClick={onSkip} disabled={saving} className={APP_BTN_OUTLINE_CLASS}>
            Ohne E-Rechnung weiter
          </button>
          {organization ? (
            <button type="submit" disabled={saving || !buyerReady} className={APP_BTN_PRIMARY_CLASS}>
              {saving ? <LoadingSpinner size="sm" /> : null}
              Speichern und weiter
            </button>
          ) : null}
        </div>
      </form>
    </AppModal>
  );
}
